// v10/src/components/admin/MaintenanceInventoryScreen.js
//
// Pantalla contenedor del módulo "Mantenimiento e inventario".
// 3 stats cards + 3 pestañas:
// - Vida límite (A6.1): integrada — datos reales del dashboard de componentes
// - Stock de repuestos (A8): "Próximamente"
// - Combustible (A9): integrada — datos reales del dashboard de tanques
//
// El stat "Stock bajo mínimo" sale del dashboard de A9.
// Los stats "Componentes vencidos" y "Vencen en 90 días" salen del dashboard de A6.1
// (expired_count y next_count + critical_count).
// "Stock de repuestos" queda en cero hasta que A8 tenga backend.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Wrench, ShieldCheck, AlertTriangle, PackageMinus,
  Clock, Boxes, Fuel as FuelIcon, Settings,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import fuelService from '../../services/fuelService';
import componentLifespanService from '../../services/componentLifespanService';
import serviceComponentsService from '../../services/serviceComponentsService';
import { POLLING_INTERVALS } from '../../config/realtime';
import useIsMobile from '../../hooks/useIsMobile';
import FuelTab from './fuel/FuelTab';
import LifespanTab from './lifespan/LifespanTab';
import InventoryTab from './inventory/InventoryTab';
import ServiceTab from './service/ServiceTab';

const TAB_FUEL     = 'fuel';
const TAB_LIFESPAN = 'lifespan';
const TAB_STOCK    = 'stock';
const TAB_SERVICE  = 'service';

const StatCard = ({ icon: Icon, label, value, color, darkMode }) => {
  const fgPrimary = darkMode ? 'white' : '#0f172a';
  const fgMuted   = darkMode ? 'rgba(255,255,255,0.45)' : '#64748b';
  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: 16, borderRadius: 18,
        background: darkMode ? 'rgba(255,255,255,0.03)' : 'white',
        border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0',
        boxShadow: '0 2px 12px rgba(0,0,0,0.05)',
        minHeight: 80,
      }}
    >
      <div
        style={{
          width: 44, height: 44, borderRadius: 14,
          background: `${color}1f`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icon style={{ width: 22, height: 22, color }} />
      </div>
      <div style={{ minWidth: 0 }}>
        <p style={{ margin: 0, fontSize: 24, fontWeight: 900, color: fgPrimary, lineHeight: 1, letterSpacing: '-0.4px' }}>
          {value}
        </p>
        <p style={{ margin: '4px 0 0', fontSize: 12, fontWeight: 600, color: fgMuted, lineHeight: 1.3 }}>
          {label}
        </p>
      </div>
    </div>
  );
};

const ComingSoon = ({ icon: Icon, darkMode }) => {
  const { t } = useTranslation('admin');
  const fgPrimary = darkMode ? 'white' : '#0f172a';
  const fgMuted   = darkMode ? 'rgba(255,255,255,0.5)' : '#64748b';
  return (
    <div
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        gap: 14, padding: '64px 24px',
        borderRadius: 20,
        border: darkMode ? '1px dashed rgba(255,255,255,0.12)' : '1px dashed #cbd5e1',
        background: darkMode ? 'rgba(255,255,255,0.02)' : '#f8fafc',
        textAlign: 'center',
      }}
    >
      <div
        style={{
          width: 64, height: 64, borderRadius: 20,
          background: darkMode ? 'rgba(99,102,241,0.16)' : 'rgba(99,102,241,0.10)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <Icon style={{ width: 30, height: 30, color: '#6366f1' }} />
      </div>
      <div>
        <p style={{ margin: 0, fontSize: 18, fontWeight: 800, color: fgPrimary }}>
          {t('maintenance_coming_soon')}
        </p>
        <p style={{ margin: '6px 0 0', fontSize: 13, color: fgMuted, maxWidth: 460 }}>
          {t('maintenance_coming_soon_desc')}
        </p>
      </div>
    </div>
  );
};

const MaintenanceInventoryScreen = ({ darkMode, userRole, locale = 'es-AR' }) => {
  const { t } = useTranslation('admin');
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState(TAB_FUEL);
  const [lowStockCount,     setLowStockCount]     = useState(0);
  const [expiredCount,      setExpiredCount]      = useState(0);
  const [expiringSoonCount, setExpiringSoonCount] = useState(0);
  // "Necesitan service" = aviones con al menos un componente en preaviso o vencido
  // (suma de los nuevos contadores por componente). Si la org no tiene componentes
  // cargados, queda en 0.
  const [serviceAttentionCount, setServiceAttentionCount] = useState(0);
  const lifespanPollRef = useRef(null);
  const servicePollRef  = useRef(null);

  // Header lee el dashboard para enterarse del low_stock_count.
  // Si falla, dejamos el contador en 0 sin romper la pantalla.
  const refreshDashboard = useCallback(async () => {
    try {
      const res = await fuelService.getDashboard();
      const lsc = res?.data?.data?.low_stock_count;
      setLowStockCount(typeof lsc === 'number' ? lsc : 0);
    } catch (_) {
      setLowStockCount(0);
    }
  }, []);

  useEffect(() => { refreshDashboard(); }, [refreshDashboard]);

  // Header de A6.1: dashboard de componentes (vencidos + próximos+críticos).
  // Polling cada 30s mientras el módulo esté montado, igual que el de A9.
  // Si falla, los stats quedan en 0 sin romper la pantalla.
  const refreshLifespanDashboard = useCallback(async () => {
    try {
      const res = await componentLifespanService.getDashboard();
      const data = res?.data?.data || {};
      const expired  = typeof data.expired_count  === 'number' ? data.expired_count  : 0;
      const critical = typeof data.critical_count === 'number' ? data.critical_count : 0;
      const next     = typeof data.next_count     === 'number' ? data.next_count     : 0;
      setExpiredCount(expired);
      setExpiringSoonCount(critical + next);
    } catch (_) {
      setExpiredCount(0);
      setExpiringSoonCount(0);
    }
  }, []);

  useEffect(() => { refreshLifespanDashboard(); }, [refreshLifespanDashboard]);

  useEffect(() => {
    if (lifespanPollRef.current) {
      clearInterval(lifespanPollRef.current);
      lifespanPollRef.current = null;
    }
    lifespanPollRef.current = setInterval(refreshLifespanDashboard, POLLING_INTERVALS.maintenance);
    return () => {
      if (lifespanPollRef.current) clearInterval(lifespanPollRef.current);
      lifespanPollRef.current = null;
    };
  }, [refreshLifespanDashboard]);

  // Dashboard "Necesitan service" — aviones con al menos un componente de
  // service por componente en preaviso o vencido. Polling cada 30s.
  const refreshServiceDashboard = useCallback(async () => {
    try {
      const res = await serviceComponentsService.getDashboard();
      const data = res?.data?.data || {};
      const attention = typeof data.attention_aircraft_count === 'number'
        ? data.attention_aircraft_count : 0;
      setServiceAttentionCount(attention);
    } catch (_) {
      setServiceAttentionCount(0);
    }
  }, []);

  useEffect(() => { refreshServiceDashboard(); }, [refreshServiceDashboard]);
  useEffect(() => {
    if (servicePollRef.current) clearInterval(servicePollRef.current);
    servicePollRef.current = setInterval(refreshServiceDashboard, POLLING_INTERVALS.maintenance);
    return () => { if (servicePollRef.current) clearInterval(servicePollRef.current); };
  }, [refreshServiceDashboard]);

  // FuelTab nos avisa cuando el listado cambia, así también refrescamos el header.
  const handleTanksChange = useCallback((tanks) => {
    if (!Array.isArray(tanks)) return;
    const count = tanks.reduce((acc, tk) => {
      const current = Number(tk.current_stock_liters) || 0;
      const minimum = Number(tk.minimum_stock_liters) || 0;
      const isBelow = typeof tk.is_below_minimum === 'boolean'
        ? tk.is_below_minimum
        : current < minimum;
      return isBelow ? acc + 1 : acc;
    }, 0);
    setLowStockCount(count);
  }, []);

  const totalAlerts = lowStockCount + expiredCount + expiringSoonCount + serviceAttentionCount;

  const TABS = [
    { id: TAB_LIFESPAN, label: t('maintenance_tab_lifespan'), desc: t('maintenance_tab_lifespan_desc'), icon: ShieldCheck, color: '#16a34a' },
    { id: TAB_SERVICE,  label: t('maintenance_tab_service'),  desc: t('maintenance_tab_service_desc'),  icon: Settings,    color: '#ea580c' },
    { id: TAB_STOCK,    label: t('maintenance_tab_stock'),    desc: '',                                  icon: Boxes,       color: '#0ea5e9' },
    { id: TAB_FUEL,     label: t('maintenance_tab_fuel'),     desc: '',                                  icon: FuelIcon,    color: '#6366f1' },
  ];

  const activeTabDesc = TABS.find((tt) => tt.id === activeTab)?.desc || '';

  const fgPrimary = darkMode ? 'white' : '#0f172a';
  const fgMuted   = darkMode ? 'rgba(255,255,255,0.5)' : '#64748b';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Header */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div
            style={{
              width: 44, height: 44, borderRadius: 14,
              background: 'linear-gradient(135deg,#4f46e5,#6366f1)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 6px 16px rgba(79,70,229,0.32)',
              flexShrink: 0,
            }}
          >
            <Wrench style={{ width: 22, height: 22, color: 'white' }} />
          </div>
          <div>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 900, color: fgPrimary, letterSpacing: '-0.4px' }}>
              {t('maintenance_title')}
            </h1>
            <p style={{ margin: '3px 0 0', fontSize: 13, color: fgMuted }}>
              {t('maintenance_subtitle_admin')}
              {' · '}
              {totalAlerts > 0 ? (
                <span style={{ color: '#ea580c', fontWeight: 600 }}>
                  {t('maintenance_active_alerts', { count: totalAlerts })}
                </span>
              ) : (
                t('maintenance_no_alerts')
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Stats cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: 12,
      }}>
        <StatCard
          icon={AlertTriangle}
          label={t('maintenance_stats_expired')}
          value={expiredCount}
          color="#ef4444"
          darkMode={darkMode}
        />
        <StatCard
          icon={Clock}
          label={t('maintenance_stats_expiring_soon')}
          value={expiringSoonCount}
          color="#f59e0b"
          darkMode={darkMode}
        />
        <StatCard
          icon={PackageMinus}
          label={t('maintenance_stats_low_stock')}
          value={lowStockCount}
          color="#6366f1"
          darkMode={darkMode}
        />
        <StatCard
          icon={Settings}
          label={t('maintenance_stats_service_attention')}
          value={serviceAttentionCount}
          color="#ea580c"
          darkMode={darkMode}
        />
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div
          style={isMobile ? {
            display: 'flex', flexWrap: 'nowrap', gap: 8,
            overflowX: 'auto', WebkitOverflowScrolling: 'touch',
            paddingBottom: 4, marginLeft: -2, marginRight: -2, paddingLeft: 2, paddingRight: 2,
          } : {
            display: 'flex', flexWrap: 'wrap', gap: 8,
          }}
        >
        {TABS.map((tab) => {
          const isActive = tab.id === activeTab;
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '9px 16px', borderRadius: 12, border: 'none', cursor: 'pointer',
                fontFamily: 'inherit', fontSize: 13, fontWeight: 700,
                background: isActive
                  ? `linear-gradient(135deg,${tab.color},${tab.color}dd)`
                  : (darkMode ? 'rgba(255,255,255,0.06)' : '#f1f5f9'),
                color: isActive ? 'white' : (darkMode ? 'rgba(255,255,255,0.55)' : '#64748b'),
                boxShadow: isActive ? `0 4px 14px ${tab.color}40` : 'none',
                transition: 'all 0.2s',
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              <Icon style={{ width: 15, height: 15 }} />
              {tab.label}
            </button>
          );
        })}
        </div>
        {activeTabDesc && (
          <p style={{ margin: '2px 2px 0', fontSize: 12, color: fgMuted }}>
            {activeTabDesc}
          </p>
        )}
      </div>

      {/* Contenido de la pestaña activa */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.18 }}
        >
          {activeTab === TAB_FUEL && (
            <FuelTab
              darkMode={darkMode}
              userRole={userRole}
              locale={locale}
              onTanksChange={handleTanksChange}
              isActive={activeTab === TAB_FUEL}
            />
          )}
          {activeTab === TAB_LIFESPAN && (
            <LifespanTab
              darkMode={darkMode}
              userRole={userRole}
              locale={locale}
              isActive={activeTab === TAB_LIFESPAN}
            />
          )}
          {activeTab === TAB_STOCK    && (
            <InventoryTab
              darkMode={darkMode}
              userRole={userRole}
              locale={locale}
              isActive={activeTab === TAB_STOCK}
            />
          )}
          {activeTab === TAB_SERVICE  && (
            <ServiceTab
              darkMode={darkMode}
              userRole={userRole}
              locale={locale}
              isActive={activeTab === TAB_SERVICE}
              onDashboardChange={(data) => {
                if (data && typeof data.attention_aircraft_count === 'number') {
                  setServiceAttentionCount(data.attention_aircraft_count);
                }
              }}
            />
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default MaintenanceInventoryScreen;
